import { useEffect, useState, useMemo, useContext } from "react";
import { Box, Typography, Grid, CircularProgress } from "@mui/material";
import { ChartCard } from "../../../../shared/components/chartCard";
import useQuery from "../../../../shared/components/hooks/useQuery";
import NoDataFound from "../../../../shared/components/noDataFoundBanner";
import { useSearchParams } from "react-router-dom";
import PresetDatePicker from "../../../../shared/components/dateRangePicker";
import { PropertyContext } from "../../../../contexts/PropertyContext";
import { getDefaultRange, getSubtitle, isDefaultMonth, isDefaultWeek, PresetType } from "../../../../shared/utils/dashboardDateUtils";
import { getKpiConfig } from "../../../../shared/utils/prediction-utils";

const DashboardCharts = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { propertyCode } = useContext(PropertyContext);
  const [currentPropertyCode, setCurrentPropertyCode] = useState<string>(
    propertyCode || ""
  );

  useEffect(() => {
    if (propertyCode) setCurrentPropertyCode(propertyCode);
  }, [propertyCode]);

  const initialFilter = (searchParams.get("filter") as PresetType) || "weekly";
  const [filter, setFilter] = useState<PresetType>(initialFilter);
  const [range, setRange] = useState<[Date | null, Date | null]>(() =>
    getDefaultRange(initialFilter)
  );
  const [loadingData, setLoadingData] = useState(true);

  const endPoint = useMemo(() => {
    if (!currentPropertyCode) return "";

    let url = `/analytics/${filter}/${currentPropertyCode}`;

    if (filter === "yearly" && range[0]) {
      const year = range[0].getFullYear();
      url += `?year=${year}`;
    } else if (range[0] && range[1]) {
      const hasCustomRange =
        (filter === "weekly" && !isDefaultWeek(range)) ||
        (filter === "monthly" && !isDefaultMonth(range)) ||
        filter === "dailyInMonth" ||
        filter === "custom";

      if (hasCustomRange) {
        const startDate = range[0]!.toLocaleDateString("en-CA");
        const endDate = range[1]!.toLocaleDateString("en-CA");
        url += `?startDate=${startDate}&endDate=${endDate}`;
      }
    }
    return url;
  }, [filter, range, currentPropertyCode]);

  useEffect(() => {
    setLoadingData(true);
  }, [endPoint]);

  const { data, error } = useQuery(endPoint);

  const isApiError = data && ((data as any).statusCode && (data as any).statusCode !== 200);

  const safeData = Array.isArray(data) ? data : [];

  useEffect(() => {
    if (data || error) setLoadingData(false);
  }, [data, error]);

  useEffect(() => {
    setSearchParams({ filter });
  }, [filter, setSearchParams]);

  const isCustomEmpty = filter === "custom" && (!range[0] || !range[1]);
  const showNoData = !loadingData && !error && !isApiError && safeData.length === 0;

  const chartsConfig = [
    {
      kpi: 'occupancy',
      dataKey: 'occupancy_actual',
      color: '#06b6d4',
      gradientId: 'occupancyGradient'
    },
    {
      kpi: 'adr',
      dataKey: 'adr_actual',
      color: '#0f172a',
      gradientId: 'adrGradient'
    },
    {
      kpi: 'revpar',
      dataKey: 'revpar_actual',
      color: '#3b82f6',
      gradientId: 'revparGradient'
    }
  ];

  return (
    <Box className="dashBoardMain" sx={{ pt: -3, px: 2, pb: 2, width: "100%" }}>
      <Box
        sx={{
          p: { xs: 2, sm: 3 },
          width: "100%",
          backgroundColor: "#9E9E9E1A",
          borderRadius: "12px",
          margin: { xs: "0 auto" },
          maxWidth: { xs: "100%", sm: "none" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: { xs: "center", sm: "space-between" },
            alignItems: "center",
            mb: 3,
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: { xs: "center", sm: "flex-start" },
              gap: { xs: 0, md: 0.5 },
              width: { xs: "100%", sm: "auto" },
              textAlign: { xs: "center", sm: "left" },
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              Graphs
            </Typography>
            {safeData.length > 0 && !isApiError && (
              <Typography variant="body2" color="text.secondary">
                {getSubtitle(safeData, filter)}
              </Typography>
            )}
          </Box>

          <PresetDatePicker
            value={range}
            preset={filter}
            onChange={(newRange, newPreset) => {
              setRange(newRange ?? [null, null]);
              setFilter(newPreset);
            }}
          />
        </Box>

        {loadingData ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (error || isApiError) ? (
          <Box display="flex" justifyContent="center" p={4}>
            <NoDataFound />
          </Box>
        ) : isCustomEmpty ? (
          <Box display="flex" justifyContent="center" p={8} textAlign="center">
            <Typography variant="h6" color="text.secondary">
              Please select a date range to view the analytics.
            </Typography>
          </Box>
        ) : showNoData ? (
          <Box display="flex" justifyContent="center" p={4}>
            <NoDataFound />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {chartsConfig.map((chart) => {
              const config = getKpiConfig(chart.kpi);
              const title = `${config.label}${config.unit ? ` (${config.unit})` : ""}`;

              return (
                <Grid item xs={12} sm={6} md={6} key={`${filter}-${chart.kpi}`}>
                  <ChartCard
                    title={title}
                    kpi={chart.kpi}
                    dataKey={chart.dataKey}
                    color={chart.color}
                    gradientId={chart.gradientId}
                    data={safeData}
                    leftLabel={`↑ ${config.label}`}
                    rightLabel="Duration →"
                    mode={filter}
                  />
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>
    </Box>
  );
};

export default DashboardCharts;